import { List, useTable, ShowButton, DateField } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import {
  Table,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Select,
  Typography,
} from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { PlanRecord } from "@/lib/types";
import { brl } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { MelDonutChart, MelBarChart } from "@/components/charts/MelCharts";
import { MONTHLY_PRICE_BRL, ANNUAL_PRICE_BRL } from "@/lib/constants";

const { Text } = Typography;

const STATUS_COLOR: Record<PlanRecord["status"], string> = {
  active: "green",
  trialing: "blue",
  past_due: "orange",
  canceled: "red",
  none: "default",
};

const STATUS_LABEL: Record<PlanRecord["status"], string> = {
  active: "Ativa",
  trialing: "Trial",
  past_due: "Inadimplente",
  canceled: "Cancelada",
  none: "—",
};

/**
 * /admin/plans — billing/Stripe (assinaturas, MRR, status).
 *
 * Como obtém os dados (MOCK MODE): a tabela usa `useTable` paginado contra
 * FIXTURES.plans (PlanRecord com status Stripe e mrr_cents). Para os cards de
 * MRR/assinantes e os gráficos, um `useList({ pagination: { mode: "off" } })`
 * traz TODAS as assinaturas e agregamos no cliente (Σ mrr_cents, contagem por
 * status / tipo de cobrança). Mesma fonte de GET /admin/billing na API real
 * (basta VITE_MOCK=false).
 */
export const PlanList: React.FC = () => {
  const [status, setStatus] = useState<PlanRecord["status"] | undefined>();

  const { tableProps, setFilters } = useTable<PlanRecord>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
  });

  // Todas as assinaturas para agregação (sem paginar).
  const { data: allData } = useList<PlanRecord>({
    resource: "plans",
    pagination: { mode: "off" },
  });
  const rows = useMemo(() => allData?.data ?? [], [allData]);

  const metrics = useMemo(() => {
    let mrrCents = 0;
    let activeSubs = 0;
    let monthly = 0;
    let annual = 0;
    const byStatus = new Map<PlanRecord["status"], number>();
    for (const r of rows) {
      mrrCents += r.mrr_cents ?? 0;
      byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);
      if (r.status === "active") {
        activeSubs += 1;
        if (r.tipo_cobranca === "annual") annual += 1;
        else if (r.tipo_cobranca === "monthly") monthly += 1;
      }
    }
    const mrr = mrrCents / 100;
    const arr = mrr * 12; // receita anualizada
    const arpu = activeSubs ? mrr / activeSubs : 0;
    const statusData = (Object.keys(STATUS_LABEL) as PlanRecord["status"][])
      .map((s) => ({ name: STATUS_LABEL[s], value: byStatus.get(s) ?? 0 }))
      .filter((d) => d.value > 0);
    return { mrr, arr, activeSubs, monthly, annual, arpu, statusData };
  }, [rows]);

  const planTypeData = useMemo(
    () => [
      { label: `Mensal (${brl.format(MONTHLY_PRICE_BRL)})`, count: metrics.monthly },
      { label: `Anual (${brl.format(ANNUAL_PRICE_BRL)})`, count: metrics.annual },
    ],
    [metrics.monthly, metrics.annual],
  );

  const onStatus = (s?: PlanRecord["status"]) => {
    setStatus(s);
    setFilters(
      [{ field: "status", operator: "eq", value: s ?? undefined }],
      "replace",
    );
  };

  return (
    <List title="Planos & Billing (Stripe)">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="MRR"
            value={brl.format(metrics.mrr)}
            prefix={<DollarOutlined />}
            hint="receita recorrente mensal"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="ARR (anualizado)"
            value={brl.format(metrics.arr)}
            prefix={<RiseOutlined />}
            hint="MRR × 12"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Assinantes ativos"
            value={metrics.activeSubs}
            prefix={<TeamOutlined />}
            hint={`${metrics.monthly} mensal · ${metrics.annual} anual`}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="ARPU"
            value={brl.format(metrics.arpu)}
            prefix={<CalendarOutlined />}
            hint="MRR / assinantes ativos"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Assinaturas por status"
          >
            <MelDonutChart data={metrics.statusData} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Assinantes ativos por cobrança"
          >
            <MelBarChart
              data={planTypeData}
              xKey="label"
              series={[{ key: "count", name: "Assinantes" }]}
              horizontal
              height={200}
            />
          </Card>
        </Col>
      </Row>

      <Card
        className="mel-glass"
        bordered={false}
        title="Assinaturas"
        extra={
          <Select
            allowClear
            placeholder="Filtrar por status"
            style={{ width: 200 }}
            value={status}
            onChange={onStatus}
            options={(Object.keys(STATUS_LABEL) as PlanRecord["status"][])
              .filter((s) => s !== "none")
              .map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          />
        }
      >
        <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}>
          <Table.Column<PlanRecord>
            dataIndex="email"
            title="Usuário"
            sorter
            render={(email: string, row) => (
              <Space direction="vertical" size={0}>
                <Text strong>{email}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.user_id}
                </Text>
              </Space>
            )}
          />
          <Table.Column
            dataIndex="tipo_cobranca"
            title="Cobrança"
            render={(t: PlanRecord["tipo_cobranca"]) => (
              <Tag color={t === "annual" ? "purple" : t === "monthly" ? "cyan" : "default"}>
                {t}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="status"
            title="Status"
            sorter
            render={(s: PlanRecord["status"]) => (
              <Tag color={STATUS_COLOR[s] ?? "default"}>{STATUS_LABEL[s] ?? s}</Tag>
            )}
          />
          <Table.Column
            dataIndex="mrr_cents"
            title="MRR"
            sorter
            render={(c: number) => brl.format((c ?? 0) / 100)}
          />
          <Table.Column
            dataIndex="stripe_subscription_id"
            title="Stripe sub"
            render={(v: string | null) =>
              v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : "—"
            }
          />
          <Table.Column
            dataIndex="data_renovacao"
            title="Renovação"
            sorter
            render={(v: string | null) =>
              v ? <DateField value={v} format="DD/MM/YYYY" /> : "—"
            }
          />
          <Table.Column<PlanRecord>
            title="Ações"
            fixed="right"
            render={(_, row) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={row.id} />
              </Space>
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
